// Story 15.1 — the provisioning write behind Screen 33 (Add organization).
//
// 201, and the body carries NO password, NO hash and NO email address by contract:
// the temporary password reaches the first org_admin on the invitation email and
// nowhere else, and the response echoes back none of the three addresses the
// operator just typed. The screen renders `organization_id` and navigates.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { ADMIN_CONSOLE_QUERY_KEY } from "@/hooks/queries/useAdminConsole";
import { ADMIN_ORG_TOTALS_QUERY_KEY } from "@/hooks/queries/useAdminOrgTotals";
import { ADMIN_ORGS_QUERY_KEY } from "@/hooks/queries/useAdminOrgs";

/** POST body — mirrors `create_organization/schemas.py :: CreateOrganizationRequest`. */
export interface CreateOrganizationRequest {
  name: string;
  /**
   * Story 15.6 AC11 — OPTIONAL at creation: CITS provisions organizations ahead
   * of the CRM record existing. `null` and omission are the same thing to the
   * endpoint; the empty string is NOT sent, because the column is UNIQUE and
   * admits many NULLs but exactly one `""`.
   */
  external_reference_id?: string | null;
  billing_address: string;
  seat_capacity: number;
  /** `YYYY-MM-DD` — a bare calendar date, never a timezone-bearing instant. */
  contract_start_date: string;
  contract_end_date: string;
  billing_contact_email: string;
  security_contact_email: string;
  admin_email: string;
  admin_first_name: string;
  admin_last_name: string;
}

/** 201 payload — `create_organization/schemas.py :: CreateOrganizationData`. */
export interface CreateOrganizationData {
  organization_id: string;
  name: string;
  /** Always "professional" — this endpoint creates no other type. */
  org_type: string;
  seat_capacity: number;
  /** Always 0 at creation: the owner holds no seat row. */
  seat_consumed: number;
  remaining: number;
  contract_start_date: string;
  contract_end_date: string;
  /** As STORED — `null` when the caller left it blank, never the empty string. */
  external_reference_id: string | null;
  owner_user_id: string;
  force_password_change: boolean;
  invitation_email_sent: boolean;
}

// Action-in-path (Rule 06), matching the mounted route
// `POST /api/v1/admin/orgs/create`.
const CREATE_ORGANIZATION_PATH = "/admin/orgs/create";

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  // Story 15.6 (AC28) — the directory (Screen 23) now exists, so this write
  // invalidates it. The ["admin-orgs"] PREFIX, not one filter's key: a partial
  // key match refetches every cached filter permutation, so the organization the
  // operator just provisioned appears whichever search/page state the directory
  // was left in.
  return useMutation({
    mutationFn: (body: CreateOrganizationRequest) =>
      apiFetch<CreateOrganizationData>(CREATE_ORGANIZATION_PATH, { method: "POST", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORGS_QUERY_KEY] });
      // TWO KEYS, because provisioning moves a number on each of two screens
      // and neither reads this screen's list response.
      //
      // The console keeps "Renewals in 90 days": a new organization arrives
      // with contract dates and can land inside the window.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONSOLE_QUERY_KEY] });
      // Screen 23's four stat tiles read their OWN endpoint since DW-423 — they
      // used to ride the console payload, which is what made the directory call
      // `GET /admin/console`. "Organizations" is the one this write moves.
      //
      // Without both, the stale key keeps a `staleTime: 30_000` snapshot
      // (App.tsx) while the table below it refetches, so a tile contradicts the
      // rows directly beneath it for up to 30s and then self-heals — the shape
      // of bug that gets reported as "sometimes wrong" and never reproduces on
      // demand.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_TOTALS_QUERY_KEY] });
    },
  });
}
